import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from './LocalizationProvider';
import { ContactData, VehicleData } from './DeviceValue';
import { useAttributePreference } from '../util/preferences';
import usePositionAttributes from '../attributes/usePositionAttributes';
import StatusRow from './StatusRow';
import PositionValue from './PositionValue';
import PositionLabel from './PositionLabel';

const useStyles = makeStyles((theme) => ({
  table: {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  cardCell: {
    [theme.breakpoints.up('lg')]: {
      width: '22%',
      minWidth: '230px',
    },
    borderBottom: 'none',
  },
  tabLabel: {
    textTransform: 'none',
    minWidth: 'auto',
    padding: '6px 8px',
    fontSize: '0.9rem',
  },
  tabContent: {
    minHeight: 250, // Set minimum height for tab content
    display: 'flex',
    flexDirection: 'column',
  },
  mediaContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  media: {
    width: '100%',
    height: 150,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  clickableImage: {
    cursor: 'pointer',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  dialogImage: {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
}));

const InfoTab = ({ t, classes, position }) => {
  const positionAttributes = usePositionAttributes(t);
  const positionItems = useAttributePreference('positionItems', 'speed,address,totalDistance,lastStop,serverTime');

  return (
    <Table size="small" classes={{ root: classes.table }}>
      {position && (
        <TableBody>
          {positionItems.split(',').filter((key) => position.hasOwnProperty(key) || position.attributes.hasOwnProperty(key)).map((key) => (
            <StatusRow
              key={key}
              name={<PositionLabel position={position} attributeKey={key} positionAttributes={positionAttributes} />}
              content={(
                <PositionValue
                  position={position}
                  property={position.hasOwnProperty(key) ? key : null}
                  attribute={position.hasOwnProperty(key) ? null : key}
                />
              )}
            />
          ))}
        </TableBody>
      )}
    </Table>
  );
};

const VehicleTab = ({ t, classes, device }) => {
  const vehicleData = VehicleData((device));
  return (
    <Table size="small" classes={{ root: classes.table }}>
      <TableBody>
        {vehicleData.map((vehicle) => (
          <StatusRow
            key={vehicle.id}
            name={t(vehicle.id)}
            content={vehicle.value}
            rawContent={vehicle.raw || false}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const ContactTab = ({ t, classes, device }) => {
  const contactData = ContactData((device));
  return (
    <Table size="small" classes={{ root: classes.table }}>
      <TableBody>
        {contactData.map((contact) => (
          <StatusRow
            key={contact.id}
            name={t(contact.id)}
            content={contact.value}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const VehicleImageTab = ({ classes, device }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Use the device image from attributes
  const deviceImage = device?.attributes?.deviceImage;
  // Check if deviceImage already contains a full URL path
  const imageUrl = deviceImage ? (
    deviceImage.startsWith('/api/media') ? deviceImage : `/api/media/${device.uniqueId}/${deviceImage}`
  ) : null;

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <div className={classes.mediaContainer}>
      {imageUrl ? (
        <>
          <Box width="100%" display="flex" justifyContent="center" flexDirection="column" alignItems="center">
            <Box
              component="div"
              role="button"
              tabIndex={0}
              className={classes.clickableImage}
              onClick={handleOpenDialog}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleOpenDialog();
                }
              }}
            >
              <img
                src={imageUrl}
                alt={device.name}
                onError={(e) => {
                  // Replace the parent Box with the image URL for debugging
                  if (e.target.parentElement) {
                    e.target.parentElement.innerHTML = `URL: ${imageUrl}`;
                  }
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: 100,
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  margin: '0 auto',
                }}
              />
            </Box>
          </Box>
          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth={false}
            PaperProps={{
              style: {
                maxWidth: 'fit-content',
                margin: '16px',
              },
            }}
          >
            <DialogContent style={{ position: 'relative', padding: '24px', textAlign: 'center' }}>
              <IconButton
                className={classes.closeButton}
                onClick={handleCloseDialog}
                size="large"
              >
                <CloseIcon />
              </IconButton>
              <img
                src={imageUrl}
                alt={device.name}
                className={classes.dialogImage}
              />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="200px">
          No image URL available (deviceImage attribute not set)
        </Box>
      )}
    </div>
  );
};

const StatusTabPanel = ({ largeScreen, device, position }) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const handleChange = (event, newValue) => {
    setCurrentTabIndex(newValue);
  };

  const t = useTranslation();
  const theme = useTheme();
  const classes = useStyles(theme);

  // Check if device has an image
  const hasImage = !!device?.attributes?.deviceImage;

  // Device image availability check

  // Adjust tab index if image tab is removed and current selection is higher
  useEffect(() => {
    if (!hasImage && currentTabIndex === 3) {
      setCurrentTabIndex(0);
    }
  }, [hasImage, currentTabIndex]);

  return (
    !largeScreen ? (
      <>
        <Tabs
          value={currentTabIndex}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { transition: 'none' } }}
        >
          <Tab disableRipple label={t('sharedInfoTitle')} className={classes.tabLabel} />
          <Tab disableRipple label={t('sharedVehicle')} className={classes.tabLabel} />
          <Tab disableRipple label={t('deviceContact')} className={classes.tabLabel} />
          {hasImage && (
            <Tab disableRipple label={t('vehicleImage')} className={classes.tabLabel} />
          )}
        </Tabs>
        <div className={classes.tabContent}>
          {currentTabIndex === 0 && (
            <InfoTab
              t={t}
              classes={classes}
              device={device}
              position={position}
            />
          )}
          {currentTabIndex === 1 && (
            <VehicleTab
              t={t}
              classes={classes}
              device={device}
            />
          )}
          {currentTabIndex === 2 && (
            <ContactTab
              t={t}
              classes={classes}
              device={device}
            />
          )}
          {currentTabIndex === 3 && hasImage && (
            <VehicleImageTab
              classes={classes}
              device={device}
            />
          )}
        </div>
      </>
    ) : (
      <Table size="small" style={{ borderSpacing: 0, borderCollapse: 'collapse' }}>
        <TableBody>
          <TableRow>
            <TableCell className={classes.cardCell} style={{ padding: 4 }}>
              <InfoTab
                t={t}
                classes={classes}
                device={device}
                position={position}
              />
            </TableCell>
            <TableCell className={classes.cardCell} style={{ padding: 4 }}>
              <VehicleTab
                t={t}
                classes={classes}
                device={device}
              />
            </TableCell>
            <TableCell className={classes.cardCell} style={{ padding: 4 }}>
              <ContactTab
                t={t}
                classes={classes}
                device={device}
              />
            </TableCell>
            {hasImage && (
              <TableCell className={classes.cardCell} style={{ padding: 4 }}>
                <VehicleImageTab
                  classes={classes}
                  device={device}
                />
              </TableCell>
            )}
          </TableRow>
        </TableBody>
      </Table>
    )
  );
};

export default StatusTabPanel;
